/**
 * Certifications register: client certifications across all EPC contracts, with confirm / convert-to-invoice
 * for the commercial approver.
 */
import React, { useMemo, useState } from 'react';
import { Button, Card, Input, Select, Space, Table, Tag, Typography, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import { ConvertCertificationToSalesInvoiceModal } from '@/components/epc/ConvertCertificationToSalesInvoiceModal';
import { epcContractAdminService } from '@/services/epcContractAdmin.service';
import { useAuthStore } from '@/store/authStore';
import { formatMoney } from '@/utils/format';

const { Title, Text } = Typography;

const CERT_STATUSES = ['Draft', 'Certified'];
const CERT_MANAGE_PERMISSIONS = ['epc.certification.manage', 'epc.admin'];

type ApiError = { response?: { data?: { message?: string } }; message?: string };
const errMsg = (e: unknown, fallback: string) =>
  (e as ApiError)?.response?.data?.message || (e as ApiError)?.message || fallback;

const EpcCertificationsRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canManage = !!user?.is_super_admin || CERT_MANAGE_PERMISSIONS.some((p) => user?.permissions?.includes(p));
  const [status, setStatus] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [convertRow, setConvertRow] = useState<Record<string, unknown> | null>(null);

  const listQ = useQuery({
    queryKey: ['epc-certifications-register', status],
    queryFn: () => epcContractAdminService.listCertifications(undefined, { status }),
    staleTime: 15_000,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ['epc-certifications-register'] });

  const confirmM = useMutation({
    mutationFn: (id: string) => epcContractAdminService.confirmCertification(id),
    onSuccess: () => {
      message.success('Certification confirmed');
      refresh();
    },
    onError: (e) => message.error(errMsg(e, 'Confirm failed')),
  });
  const convertM = useMutation({
    mutationFn: (v: { certId: string; departmentId: string }) =>
      epcContractAdminService.convertCertificationToSalesInvoice(v.certId, { departmentId: v.departmentId }),
    onSuccess: (data) => {
      message.success('Draft Progress Tax Invoice ready');
      setConvertRow(null);
      refresh();
      const row = data as Record<string, unknown>;
      const bridge = row.salesInvoice as Record<string, unknown> | undefined;
      const siId = bridge?.salesInvoiceId || row.salesInvoiceId;
      if (siId) navigate(`/growth/invoices/${String(siId)}`);
    },
    onError: (e) => message.error(errMsg(e, 'Convert failed')),
  });

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const all = (listQ.data || []) as Record<string, unknown>[];
    if (!q) return all;
    return all.filter((r) =>
      [r.certification_number, r.application_number, r.contract_number, r.customer_name]
        .map((v) => String(v || '').toLowerCase())
        .some((v) => v.includes(q))
    );
  }, [listQ.data, search]);

  const money = (v: unknown, r: Record<string, unknown>) => formatMoney(v, { currency: String(r.currency || 'AED') });

  return (
    <div style={{ padding: 16 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        Certifications
      </Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
        Client certifications raised from approved IPCs. Confirm, then convert to a Draft Progress Tax Invoice
        (Sales / Finance post and collect).
      </Text>
      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={CERT_STATUSES.map((s) => ({ value: s, label: s }))}
          />
          <Input.Search
            allowClear
            placeholder="Certification, IPC, contract or customer"
            style={{ width: 320 }}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Space>
        <Table
          size="small"
          loading={listQ.isLoading}
          rowKey={(r) => String(r.id)}
          dataSource={rows}
          pagination={{ pageSize: 25 }}
          scroll={{ x: 1200 }}
          columns={[
            { title: 'Certification', dataIndex: 'certification_number', width: 150 },
            {
              title: 'IPC',
              dataIndex: 'application_number',
              width: 130,
              render: (v, r) =>
                r.payment_application_id ? (
                  <Link
                    to={`/epc/commercial/contracts/${String(r.contract_id)}/payment-applications/${String(r.payment_application_id)}`}
                  >
                    {String(v || '—')}
                  </Link>
                ) : (
                  '—'
                ),
            },
            {
              title: 'Contract',
              dataIndex: 'contract_number',
              width: 130,
              render: (v, r) => <Link to={`/epc/commercial/contracts/${String(r.contract_id)}`}>{String(v || '—')}</Link>,
            },
            { title: 'Customer', dataIndex: 'customer_name', width: 200, ellipsis: true },
            {
              title: 'Status',
              dataIndex: 'status',
              width: 110,
              render: (s: string) => <Tag color={s === 'Certified' ? 'green' : undefined}>{s}</Tag>,
            },
            { title: 'Certified gross', dataIndex: 'certified_gross_amount', width: 140, align: 'right' as const, render: money },
            { title: 'Certified net', dataIndex: 'certified_net_amount', width: 140, align: 'right' as const, render: money },
            {
              title: 'Tax invoice',
              dataIndex: 'sales_invoice_id',
              width: 170,
              render: (v, r) =>
                v ? (
                  <Link to={`/growth/invoices/${String(v)}`}>
                    {String(r.sales_invoice_doc_number || String(v).slice(0, 8))}
                    {r.sales_invoice_status ? ` (${String(r.sales_invoice_status)})` : ''}
                  </Link>
                ) : (
                  'Not converted'
                ),
            },
            ...(canManage
              ? [
                  {
                    title: 'Actions',
                    key: 'actions',
                    width: 170,
                    render: (_: unknown, r: Record<string, unknown>) => (
                      <Space size="small">
                        {String(r.status) !== 'Certified' ? (
                          <Button
                            type="link"
                            size="small"
                            loading={confirmM.isPending && confirmM.variables === String(r.id)}
                            onClick={() => confirmM.mutate(String(r.id))}
                          >
                            Confirm
                          </Button>
                        ) : null}
                        {String(r.status) === 'Certified' && !r.sales_invoice_id ? (
                          <Button type="link" size="small" onClick={() => setConvertRow(r)}>
                            Convert SI
                          </Button>
                        ) : null}
                      </Space>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>
      <ConvertCertificationToSalesInvoiceModal
        open={Boolean(convertRow)}
        confirming={convertM.isPending}
        defaultDepartmentId={convertRow?.contract_department_id ? String(convertRow.contract_department_id) : null}
        onCancel={() => setConvertRow(null)}
        onConfirm={(payload) => {
          if (!convertRow) return;
          convertM.mutate({ certId: String(convertRow.id), ...payload });
        }}
      />
    </div>
  );
};

export default EpcCertificationsRegisterPage;
